/**
 * Service de gestion des communications et notifications
 */

import { API_CONFIG, getAuthHeaders, handleApiError, ApiResponse } from './apiConfig'

// Configuration des contacts de support
export interface SupportContact {
  phoneNumber: string
  whatsappNumber: string
  email: string
  workingHours: {
    phone: string
    chat: string
    whatsapp: string
  }
}

export interface SupportRequest {
  subject: string
  message: string
  priority?: 'low' | 'normal' | 'high' | 'urgent'
}

export interface Feedback {
  rating: number
  comment?: string
  category?: 'app' | 'service' | 'support' | 'other'
}

export interface Notification {
  id: number
  title: string
  message: string
  type: string
  read: boolean
  created_at: string
}

// Configuration par défaut
const defaultConfig: SupportContact = {
  phoneNumber: '+225 07 XX XX XX XX',
  whatsappNumber: '2250707070707', // Format international sans espaces ni +
  email: 'support@coopfinance.ci',
  workingHours: {
    phone: 'Lundi - Vendredi, 8h - 18h',
    chat: 'Lundi - Samedi, 8h - 20h',
    whatsapp: '24h/24, 7j/7'
  }
}

class CommunicationService {
  private config: SupportContact

  constructor(config?: Partial<SupportContact>) {
    this.config = { ...defaultConfig, ...config }
  }

  /**
   * Obtenir la configuration actuelle
   */
  getConfig(): SupportContact {
    return this.config
  }

  /**
   * Mettre à jour la configuration
   */
  updateConfig(config: Partial<SupportContact>): void {
    this.config = { ...this.config, ...config }
  }

  /**
   * Lancer un appel téléphonique
   */
  makePhoneCall(customNumber?: string): void {
    const number = customNumber || this.config.phoneNumber
    const cleanNumber = number.replace(/\s/g, '')
    window.location.href = `tel:${cleanNumber}`
  }

  /**
   * Ouvrir WhatsApp avec un message prédéfini
   */
  openWhatsApp(message?: string, customNumber?: string): void {
    const number = customNumber || this.config.whatsappNumber
    const defaultMessage = 'Bonjour, j\'ai besoin d\'aide concernant mon compte Coop Finance.'
    const encodedMessage = encodeURIComponent(message || defaultMessage)
    const whatsappUrl = `https://wa.me/${number}?text=${encodedMessage}`
    window.open(whatsappUrl, '_blank')
  }

  /**
   * Ouvrir l'application email
   */
  sendEmail(subject?: string, body?: string, customEmail?: string): void {
    const email = customEmail || this.config.email
    const encodedSubject = encodeURIComponent(subject || 'Demande d\'assistance')
    const encodedBody = encodeURIComponent(body || '')
    window.location.href = `mailto:${email}?subject=${encodedSubject}&body=${encodedBody}`
  }

  /**
   * Ouvrir le SMS
   */
  sendSMS(message?: string, customNumber?: string): void {
    const number = customNumber || this.config.phoneNumber
    const cleanNumber = number.replace(/\s/g, '')
    const encodedMessage = encodeURIComponent(message || '')
    
    // Différent pour iOS et Android
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
    const smsUrl = isIOS 
      ? `sms:${cleanNumber}&body=${encodedMessage}`
      : `sms:${cleanNumber}?body=${encodedMessage}`
    
    window.location.href = smsUrl
  }

  /**
   * Vérifier si on est dans les heures de disponibilité
   * Cette fonction peut être améliorée pour vérifier les heures réelles
   */
  isAvailable(channel: 'phone' | 'chat' | 'whatsapp'): boolean {
    // Pour l'instant, on retourne true pour WhatsApp (24/7)
    if (channel === 'whatsapp') {
      return true
    }

    // Pour les autres, on pourrait vérifier l'heure actuelle
    // Implémentation simplifiée
    const now = new Date()
    const hour = now.getHours()
    const day = now.getDay() // 0 = Dimanche, 6 = Samedi

    if (channel === 'phone') {
      // Lundi - Vendredi, 8h - 18h
      return day >= 1 && day <= 5 && hour >= 8 && hour < 18
    }

    if (channel === 'chat') {
      // Lundi - Samedi, 8h - 20h
      return day >= 1 && day <= 6 && hour >= 8 && hour < 20
    }

    return false
  }

  /**
   * Obtenir le statut de disponibilité
   */
  getAvailabilityStatus(channel: 'phone' | 'chat' | 'whatsapp'): {
    available: boolean
    message: string
  } {
    const available = this.isAvailable(channel)
    
    if (available) {
      return {
        available: true,
        message: 'Disponible maintenant'
      }
    }

    const hours = this.config.workingHours[channel]
    return {
      available: false,
      message: `Disponible ${hours}`
    }
  }

  /**
   * Obtenir les informations de support depuis l'API
   */
  async fetchSupportInfo(): Promise<ApiResponse<SupportContact>> {
    try {
      const response = await fetch(`${API_CONFIG.baseURL}/support/info`, {
        method: 'GET',
        headers: getAuthHeaders()
      })

      const data = await response.json()
      
      // Mettre à jour la configuration locale si les données sont valides
      if (data.status === 0 && data.data) {
        this.updateConfig({
          phoneNumber: data.data.phone,
          whatsappNumber: data.data.whatsapp,
          email: data.data.email,
          workingHours: data.data.workingHours
        })
      }
      
      return data
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Envoyer une demande de support
   */
  async sendSupportRequest(request: SupportRequest): Promise<ApiResponse> {
    try {
      const response = await fetch(`${API_CONFIG.baseURL}/support/request`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(request)
      })

      return await response.json()
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Envoyer un feedback
   */
  async sendFeedback(feedback: Feedback): Promise<ApiResponse> {
    try {
      const response = await fetch(`${API_CONFIG.baseURL}/support/feedback`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(feedback)
      })

      return await response.json()
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Récupérer les notifications
   */
  async getNotifications(): Promise<ApiResponse<{ notifications: Notification[], unread_count: number }>> {
    try {
      const response = await fetch(`${API_CONFIG.baseURL}/notifications`, {
        method: 'GET',
        headers: getAuthHeaders()
      })

      return await response.json()
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Marquer une notification comme lue
   */
  async markNotificationAsRead(notificationId: number): Promise<ApiResponse> {
    try {
      const response = await fetch(`${API_CONFIG.baseURL}/notifications/${notificationId}/read`, {
        method: 'POST',
        headers: getAuthHeaders()
      })

      return await response.json()
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Marquer toutes les notifications comme lues
   */
  async markAllNotificationsAsRead(): Promise<ApiResponse> {
    try {
      const response = await fetch(`${API_CONFIG.baseURL}/notifications/read-all`, {
        method: 'POST',
        headers: getAuthHeaders()
      })

      return await response.json()
    } catch (error) {
      return handleApiError(error)
    }
  }
}

// Export d'une instance singleton
export default new CommunicationService()

